import dynamic from 'next/dynamic'
import React from 'react'

import { useIntl } from 'react-intl'
import { Link } from 'solito/link'

import Avatar from 'app/components/base/Avatar'
import Box from 'app/components/base/Box'
import Skeleton from 'app/components/base/Skeleton'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import Location from 'app/components/common/Location'
import SellerHolidayHint from 'app/features/ListingFeature/components/SellerHolidayHint'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { messages as msg } from 'app/utils/constants/options.constants'

import AuctionEndHint from '../components/AuctionEndHint'
import Tag from '../components/Tag'
import { formatLocation } from '../helpers/listing.helpers'

const ReservedLabel = dynamic(
  () => import('app/components/common/ReservedLabel'),
  {
    ssr: true
  }
)

const SoldOutLabel = dynamic(
  () => import('app/components/common/SoldOutLabel'),
  {
    ssr: true
  }
)

const InternationalShippingLabel = dynamic(
  () => import('app/components/common/InternationalShippingLabel'),
  {
    ssr: true
  }
)

const tags = {
  new_item: msg.new,
  used_item: msg.used,
  aftermarket: msg.aftermarket,
  remanufactured: msg.remanufactured,
  genuine: msg.genuine
}

interface TitleInfoProps {
  avatar: string
  subTitle: string
  shipping: ShippingModel
  title?: string
  condition: string
  itemType: string
  warranty?: string
  auctionEndDate?: string | Date
  isSold?: boolean
  loading?: boolean
  isReserved?: boolean
  isHolidayMode?: boolean
  internationalShipping?: boolean
  isPersonalSeller?: boolean
  sellerSlug?: string
}

const TitleInfo: RFC<TitleInfoProps> = ({
  avatar,
  subTitle,
  shipping,
  title,
  condition,
  itemType,
  warranty,
  auctionEndDate,
  isSold,
  loading,
  isReserved,
  isHolidayMode,
  internationalShipping,
  isPersonalSeller,
  sellerSlug
}) => {
  const { formatMessage } = useIntl()

  const location = formatLocation({
    addressObj: shipping,
    withoutZipCode: isPersonalSeller
  })

  const showAuctionHint = !!auctionEndDate && !isReserved && !isSold
  const sellerLink = `/store/${sellerSlug}/products`

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: '-65px',
          right: '16px',
          zIndex: 100,
          ...(!IS_NATIVE_APP && {
            zIndex: 1,
            display: { md: 'none', xs: 'flex' },
            boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.15)',
            filter: 'drop-shadow(0px 0px 2px rgba(199, 207, 216, 0.7))',
            borderRadius: '50%'
          })
        }}
      >
        <Link href={sellerLink} aria-label="seller-store-link">
          <Avatar
            width={70}
            height={70}
            sizes="96px"
            src={avatar}
            {...(IS_NATIVE_APP && { layout: 'fill', sizes: '256px' })}
          />
        </Link>
      </Box>

      <SellerHolidayHint
        visible={isHolidayMode}
        sx={{ display: { xs: 'flex', md: 'none' }, mt: 2 }}
      />

      <Stack spacing={{ md: 2 }}>
        <Stack direction="column" spacing={0.5} sx={{ mt: { xs: 2, md: 0 } }}>
          <Skeleton loading={loading} height={24} width="100%">
            <Text variant="h3" component="h1" sx={{ fontWeight: 500 }}>
              {title}
            </Text>
          </Skeleton>

          <Skeleton loading={loading} height={20} width="100%">
            <Text variant="h5" component="h2" sx={{ color: 'text.third' }}>
              {subTitle}
            </Text>
          </Skeleton>
        </Stack>

        <Location
          location={location}
          sx={{ pt: 1, mb: 2 }}
          loading={loading}
          textColor="primary.main"
          withGoogleMapLink
        />

        {!isSold && internationalShipping && (
          <InternationalShippingLabel sx={{ mb: { xs: 2.25, md: 0 } }} />
        )}

        {showAuctionHint && (
          <AuctionEndHint auctionEndDate={String(auctionEndDate)} />
        )}

        {isSold && <SoldOutLabel sx={{ mb: { xs: 2.25, md: 0 } }} />}

        {isReserved && <ReservedLabel sx={{ mb: { xs: 2.25, md: 0 } }} />}

        {!isSold && (
          <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
            <Skeleton loading={loading} height={36} width="25%">
              <Tag tag={formatMessage(tags[condition || 'new_item'])} />
            </Skeleton>

            <Skeleton loading={loading} height={36} width="25%">
              <Tag tag={formatMessage(tags[itemType || 'aftermarket'])} />
            </Skeleton>

            <Skeleton loading={loading} height={36} width="25%">
              {warranty && (
                <Tag tag={formatMessage({ defaultMessage: 'Warranty' })} />
              )}
            </Skeleton>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export default TitleInfo
